import { useState, useRef, useEffect, useCallback, useMemo, type ReactElement } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  AppWindow,
  ListTodo,
  ChevronDown,
  Plus,
  MoreHorizontal,
  Settings,
  Palette,
  LogOut,
  User,
  Building2,
  LayoutDashboard,
  Bot,
  ListChecks,
  Activity,
  Users,
  UsersRound,
  Bell,
  Link2,
  ArrowLeft,
  X,
  Pencil,
  Trash2,
  Pin,
  PinOff,
  Search,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { agentRoleLabel } from "@/lib/agent-display";
import { useTenant } from "@/context/tenant-context";
import { useAuth } from "@/context/auth-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import {
  useAgentPrefs,
  useSetAgentPinned,
  useTouchAgent,
} from "@/hooks/use-agent-prefs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { chatApi, type ChatSession } from "@/api/chat";
import { agentChannelsApi, type AgentChannel } from "@/api/agent-channels";
import { ThemePicker } from "@/components/theme-picker";
import { Button } from "@/components/ui/button";

export function TenantSidebar({
  onOpenAccountSettings,
  onOpenTenantSettings,
}: {
  onOpenAccountSettings?: () => void;
  onOpenTenantSettings?: () => void;
}) {
  const {
    tenantId,
    tenantName,
    tenantLogoUrl,
    userRole,
    isTenantAdmin,
    isAgentAdmin,
    isInstanceAdmin,
  } = useTenant();
  // Legacy alias used in a few existing call sites below.
  const isAdmin = isTenantAdmin;
  // Standard users see no settings entry at all; agent admins see a
  // slimmed-down list (skills + MCP catalogues + their agents); tenant
  // admins see the full Admin block.
  const showAnySettings = isTenantAdmin || isAgentAdmin;
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const base = `/tenants/${tenantId}`;

  // sidebarMode swaps the middle section of the sidebar between the default
  // chat nav (agent/channels/recents) and an alternative nav list (tenant
  // settings = the former "More" contents). The user profile row drives
  // the switch; a "Back to app" button at the top of settings mode restores
  // the saved origin path so users return to the chat they were in.
  const [sidebarMode, setSidebarMode] = useState<"default" | "settings">("default");
  const originPathRef = useRef<string>("");
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  // Let a guided walkthrough close the agent picker before moving on, so its
  // dropdown doesn't cover the next highlighted area (channels/chats).
  useEffect(() => {
    const onClose = () => setAgentPickerOpen(false);
    window.addEventListener("proclaw:close-agent-picker", onClose);
    return () => window.removeEventListener("proclaw:close-agent-picker", onClose);
  }, []);
  // Type-to-filter searches: agent picker dropdown (always visible when the
  // dropdown is open), plus icon-toggled filter rows for Channels and Recents.
  const [agentQuery, setAgentQuery] = useState("");
  const [channelSearchOpen, setChannelSearchOpen] = useState(false);
  const [channelQuery, setChannelQuery] = useState("");
  const [recentsSearchOpen, setRecentsSearchOpen] = useState(false);
  const [recentsQuery, setRecentsQuery] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const agentPickerRef = useRef<HTMLDivElement>(null);

  // Recents row actions — which row has its 3-dot menu open, which is
  // being renamed inline, and which is awaiting delete confirmation.
  const [menuForSessionId, setMenuForSessionId] = useState<string | null>(null);
  const [renameState, setRenameState] = useState<{ sessionId: string; title: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ sessionId: string; title: string } | null>(null);
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  // Channel row actions — right-click menu position, delete-confirm target,
  // and drag-reorder tracking.
  const [channelMenu, setChannelMenu] = useState<
    { channelId: string; x: number; y: number } | null
  >(null);
  const [confirmDeleteChannel, setConfirmDeleteChannel] = useState<
    { id: string; name: string } | null
  >(null);
  const [dragChannelId, setDragChannelId] = useState<string | null>(null);
  const [dragOverChannelId, setDragOverChannelId] = useState<string | null>(null);
  const [localChannels, setLocalChannels] = useState<AgentChannel[] | null>(null);
  const queryClient = useQueryClient();

  // Agents
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  // Per-user pinned + last-used preferences for this sidebar
  const { data: agentPrefs } = useAgentPrefs();
  const touchAgent = useTouchAgent();
  const setAgentPinned = useSetAgentPinned();

  // Right-click context menu for the pin/unpin action. Tracked as
  // `{ agentId, x, y }` so we can render once at a fixed position.
  const [agentMenu, setAgentMenu] = useState<
    { agentId: string; x: number; y: number } | null
  >(null);

  // Read agentId and channelId from URL if on workspace page
  const urlParams = new URLSearchParams(location.search);
  const urlAgentId = urlParams.get("agentId");
  const urlChannelId = urlParams.get("channelId");
  const activeChannelId = urlChannelId ?? "general";

  // The URL is the single source of truth for the selected agent. This used
  // to be mirrored into local state and reconciled by an effect whose
  // dependency list omitted `selectedAgentId` — a stale closure that could
  // keep serving the previous agent while the URL already held the new one,
  // so the picker and the breadcrumb disagreed. Deriving removes the whole
  // class of bug: there is nothing left to fall out of step.
  //
  // When the URL carries no agentId (fresh load, non-workspace route) fall
  // back to the user's last-used agent, then the first accessible one. This
  // is display-only — it never writes back to the URL.
  const fallbackAgentId = useMemo(() => {
    if (agents.length === 0) return null;
    const lastUsedId = agentPrefs?.lastUsedAgentId;
    return lastUsedId && agents.some((a) => a.id === lastUsedId) ? lastUsedId : agents[0].id;
  }, [agents, agentPrefs?.lastUsedAgentId]);
  const selectedAgentId = urlAgentId ?? fallbackAgentId;
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  // Start each dropdown open with a clean search; clear the list filters
  // when switching agents so a stale query can't hide the new agent's lists.
  useEffect(() => {
    if (!agentPickerOpen) setAgentQuery("");
  }, [agentPickerOpen]);
  useEffect(() => {
    setChannelSearchOpen(false);
    setChannelQuery("");
    setRecentsSearchOpen(false);
    setRecentsQuery("");
  }, [selectedAgentId]);

  // Dismiss the right-click menu on any outside click/scroll/escape.
  useEffect(() => {
    if (!agentMenu) return;
    const close = () => setAgentMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [agentMenu]);

  // Channels for selected agent
  const { data: channelsData } = useQuery({
    queryKey: ["agent-channels", selectedAgentId],
    queryFn: () => agentChannelsApi.list(selectedAgentId!),
    enabled: !!selectedAgentId,
  });
  const channels = channelsData ?? [];
  // Mirror fetched channels into local state so drag-reorder updates the
  // order instantly. The server stays the source of truth and resyncs this
  // whenever the query data changes (after a reorder, delete, or create).
  useEffect(() => {
    if (channelsData) setLocalChannels(channelsData);
  }, [channelsData]);
  // Filter to the selected agent so a stale optimistic order from a
  // previously-selected agent can't flash in while the new agent loads.
  const displayChannels = (localChannels ?? channels).filter(
    (c) => c.agentId === selectedAgentId,
  );

  // Dismiss the channel right-click menu on any outside click/scroll/escape.
  useEffect(() => {
    if (!channelMenu) return;
    const close = () => setChannelMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [channelMenu]);

  // Sessions for selected agent (used for recents)
  const { data: sessions = [] } = useQuery({
    queryKey: ["chat-sessions", selectedAgentId],
    queryFn: () => chatApi.listSessions(selectedAgentId!),
    enabled: !!selectedAgentId,
    refetchInterval: (query) =>
      query.state.data?.some((session) => session.isGenerating) ? 2000 : 8000,
    refetchOnWindowFocus: true,
  });

  // Delete / rename a recent chat from the sidebar's row menu.
  const deleteSession = useMutation({
    mutationFn: (sessionId: string) => chatApi.deleteSession(sessionId),
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      // If the deleted session is the one currently open, clear it from the URL.
      const onWorkspace = location.pathname.includes("/workspace");
      if (location.search.includes(sessionId) && onWorkspace) {
        const params = new URLSearchParams(location.search);
        params.delete("sessionId");
        navigate(`${base}/workspace?${params.toString()}`, { replace: true });
      }
    },
  });
  const renameSessionMut = useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      chatApi.renameSession(sessionId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      setRenameState(null);
    },
  });

  const commitRename = useCallback(() => {
    if (!renameState) return;
    const trimmed = renameState.title.trim();
    if (!trimmed) {
      setRenameState(null);
      return;
    }
    renameSessionMut.mutate({ sessionId: renameState.sessionId, title: trimmed });
  }, [renameState, renameSessionMut]);

  // Filter recents by active channel
  const filteredRecents = activeChannelId === "general"
    ? sessions.filter((s) => !s.channelId)
    : sessions.filter((s) => s.channelId === activeChannelId);

  // Text filters. Agent search matches name or role; channel/recents search
  // match the visible label. Searching shows every match (the capped,
  // scrollable lists handle length); without a query Recents keeps its
  // usual most-recent-20 view.
  const agentQ = agentQuery.trim().toLowerCase();
  const filteredAgents = agentQ
    ? agents.filter(
        (a) =>
          a.name.toLowerCase().includes(agentQ) ||
          (agentRoleLabel(a) ?? "").toLowerCase().includes(agentQ),
      )
    : agents;
  const channelQ = channelQuery.trim().toLowerCase();
  const visibleChannels = channelQ
    ? displayChannels.filter((c) => c.name.toLowerCase().includes(channelQ))
    : displayChannels;
  const recentsQ = recentsQuery.trim().toLowerCase();
  const visibleRecents = recentsQ
    ? filteredRecents.filter((s) => s.title.toLowerCase().includes(recentsQ))
    : filteredRecents.slice(0, 20);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (agentPickerRef.current && !agentPickerRef.current.contains(e.target as Node)) {
        setAgentPickerOpen(false);
      }
      // The Recents row menu uses a data attribute to self-identify — any click
      // that isn't on a sidebar-row-menu element closes any open row menu.
      const target = e.target as HTMLElement;
      if (!target.closest("[data-recent-row-menu]")) {
        setMenuForSessionId(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const initials = user?.name
    ? user.name.split(" ").map((w: string) => w[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() ?? "?";
  const tenantRoleLabel =
    userRole === "tenant_admin"
      ? "Tenant admin"
      : userRole === "agent_admin"
        ? "Agent admin"
        : "Standard user";

  // Navigate to workspace with agent context.
  //
  // When the target is "the agent's General channel without a specific session"
  // — i.e. just clicking the agent name — we route to the tenant dashboard
  // instead of the workspace. The dashboard doubles as the chat-starter for
  // General, showing the input + suggestions while the sidebar keeps the
  // General recents visible underneath. Any other combination (a named
  // channel, a specific session, or newChat) still goes to the workspace.
  const goToChat = (opts?: { sessionId?: string; channelId?: string; newChat?: boolean }) => {
    const params = new URLSearchParams();
    if (selectedAgentId) params.set("agentId", selectedAgentId);
    if (opts?.sessionId) params.set("sessionId", opts.sessionId);
    if (opts?.channelId && opts.channelId !== "general") {
      params.set("channelId", opts.channelId);
    }
    if (opts?.newChat) params.set("newChat", "1");
    const qs = params.toString();

    const isGeneralLanding =
      (opts?.channelId === "general" || opts?.channelId === undefined) &&
      !opts?.sessionId &&
      !opts?.newChat;
    const target = isGeneralLanding ? "dashboard" : "workspace";
    navigate(`${base}/${target}${qs ? `?${qs}` : ""}`);
  };

  // Agent selection opens the dashboard starter view for that agent. Chat
  // sessions/channels are explicit next steps from there, so don't carry a
  // previous session, channel, or workspace mode across an agent switch.
  const syncAgentToUrl = (agentId: string) => {
    if (agentId === urlAgentId) return;
    // Fire-and-forget — recording "last used" must not block selection, and
    // it must not feed back into a URL write (see useTouchAgent).
    touchAgent.mutate(agentId);
    // The URL is the source of truth, so this navigate is the ONE history
    // entry an agent switch creates. Nothing mirrors it into local state.
    const params = new URLSearchParams();
    params.set("agentId", agentId);
    navigate(`${base}/dashboard?${params.toString()}`);
  };

  const pinnedAgentIds = agentPrefs?.pinnedAgentIds ?? [];
  const pinnedAgents = pinnedAgentIds
    .map((id) => agents.find((a) => a.id === id))
    .filter((a): a is (typeof agents)[number] => !!a);
  const menuAgent = agentMenu ? agents.find((a) => a.id === agentMenu.agentId) : null;
  const menuAgentPinned = menuAgent ? pinnedAgentIds.includes(menuAgent.id) : false;

  // All nav items live under "More" — Work group first, then Admin group
  // for customer admins (MSP admins inherit this via isAdmin), then
  // diagnostic tools for MSP admins only.
  //
  // Hidden by design (routes still exist, just not surfaced):
  //   - Artifacts, Goals, Approvals, Inbox — low usage at this stage.
  //   - Cowork — parked until we flesh out the multi-agent view.
  // Bring these back here when the corresponding surfaces are ready.
  const moreItems = [
    // — Work —
    { label: "Chats", to: `${base}/workspace`, icon: MessageSquare, group: "Work" },
    { label: "Apps", to: `${base}/apps`, icon: AppWindow, group: "Work" },
    { label: "Agents", to: `${base}/agents`, icon: Bot, group: "Work" },
    { label: "Tasks", to: `${base}/tasks`, icon: ListTodo, group: "Work" },
    { label: "Issues", to: `${base}/issues`, icon: ListChecks, group: "Work" },
    { label: "Dashboard", to: `${base}/dashboard`, icon: LayoutDashboard, group: "Work" },
    // — Tenant administration (tenant_admin only) —
    ...(isTenantAdmin
      ? [
          { label: "Users", to: `${base}/users`, icon: Users, group: "Admin" },
          { label: "Groups", to: `${base}/groups`, icon: UsersRound, group: "Admin" },
          { label: "Notifications", to: `${base}/notifications`, icon: Bell, group: "Admin" },
          { label: "M365", to: `${base}/m365`, icon: Link2, group: "Admin" },
          { label: "Tenant settings", onClick: onOpenTenantSettings, icon: Settings, group: "Admin" },
        ]
      : []),
    // — Catalogue management (agent_admin + tenant_admin) —
    // Agent admins can manage tenant-level skills and MCP servers but
    // don't get the broader Admin block. We surface those entries here
    // so they have a dedicated landing spot in settings mode.
    ...(isAgentAdmin
      ? [
          { label: "Skills", to: `${base}/settings/skills`, icon: ListChecks, group: "Catalogue" },
          { label: "MCP tools", to: `${base}/settings/mcp`, icon: Link2, group: "Catalogue" },
        ]
      : []),
    // — Diagnostics (MSP admins only) —
    ...(isInstanceAdmin
      ? [{ label: "Sessions debug", to: `${base}/debug/sessions`, icon: Activity, group: "Diagnostics" }]
      : []),
  ];
  const isAdminNavRoute = moreItems.some(
    (item) =>
      "to" in item &&
      item.to !== `${base}/workspace` &&
      item.to !== `${base}/dashboard` &&
      (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)),
  );
  const showAdminNav = sidebarMode === "settings" || isAdminNavRoute;

  // Channel dot colors
  const channelDotColor = (ch: { color?: string; shared?: boolean }) => {
    if (ch.shared) return "#9333ea"; // purple
    return ch.color || "#10a37f";
  };

  // ── Channel row actions: delete + drag-reorder ─────────────────────
  const deleteChannelMut = useMutation({
    mutationFn: (channelId: string) =>
      agentChannelsApi.delete(selectedAgentId!, channelId),
    onSuccess: (_d, channelId) => {
      queryClient.invalidateQueries({ queryKey: ["agent-channels", selectedAgentId] });
      // Sessions in the channel get channelId=null server-side — refresh recents.
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", selectedAgentId] });
      // If we're currently viewing the deleted channel, fall back to General.
      if (activeChannelId === channelId) goToChat({ channelId: "general" });
    },
  });

  const reorderChannelsMut = useMutation({
    mutationFn: (orderedIds: string[]) =>
      agentChannelsApi.reorder(selectedAgentId!, orderedIds),
    onSuccess: (rows) => {
      queryClient.setQueryData(["agent-channels", selectedAgentId], rows);
    },
    onError: () => {
      // Reorder failed — drop the optimistic order and refetch the truth.
      setLocalChannels(null);
      queryClient.invalidateQueries({ queryKey: ["agent-channels", selectedAgentId] });
    },
  });

  const handleChannelDrop = (targetId: string) => {
    const src = dragChannelId;
    setDragChannelId(null);
    setDragOverChannelId(null);
    if (!src || src === targetId) return;
    const current = displayChannels;
    const fromIdx = current.findIndex((c) => c.id === src);
    const toIdx = current.findIndex((c) => c.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...current];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setLocalChannels(next);
    reorderChannelsMut.mutate(next.map((c) => c.id));
  };

  const menuChannel = channelMenu
    ? displayChannels.find((c) => c.id === channelMenu.channelId) ?? null
    : null;

  return (
    <>
      <aside className="flex h-full w-full min-w-0 shrink-0 flex-col bg-sidebar">
        {/* ── Brand header ──────────────────────────────────────────── */}
        <div className="flex items-center justify-center px-5 pt-6 pb-4">
          {tenantLogoUrl ? (
            <img src={tenantLogoUrl} alt={tenantName} className="h-14 w-auto max-w-[200px] rounded object-contain" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-accent">
              <span className="text-2xl font-bold text-white">{tenantName?.charAt(0) ?? "P"}</span>
            </div>
          )}
        </div>

        {!showAdminNav && (<>
        {/* ── New Chat button (hover-only fill) ─────────────────────── */}
        <div className="mx-[14px] mb-2">
          <button
            onClick={() => goToChat({ newChat: true, channelId: activeChannelId })}
            className="flex w-full items-center gap-2 rounded-[8px] bg-transparent px-[10px] py-1.5 text-[13px] font-medium text-text-primary transition-colors hover:bg-[#efede8] dark:hover:bg-[#333]"
          >
            <Plus size={14} className="text-text-secondary" />
            New chat
          </button>
        </div>

        {/* ── Agent selector (compact single-line) ──────────────────── */}
        <div className="mx-[14px] mb-1" data-tour="agent-picker">
          <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
            Agent
          </p>
          <div className="relative" ref={agentPickerRef}>
            <div
              className={cn(
                "group flex w-full items-center gap-2 rounded-[8px] bg-transparent px-2 py-1.5 transition-colors",
                activeChannelId === "general"
                  ? "bg-[#ece9e3]/70 dark:bg-[#3a3a3a]"
                  : "hover:bg-[#efede8] dark:hover:bg-[#333]",
              )}
            >
              {/* Name + status — click selects the agent's General channel */}
              <button
                onClick={() => {
                  if (!selectedAgent) {
                    setAgentPickerOpen(true);
                    return;
                  }
                  goToChat({ channelId: "general" });
                }}
                className="min-w-0 flex flex-1 items-center gap-2 text-left"
                aria-label={selectedAgent ? `Open ${selectedAgent.name}'s General channel` : "Select agent"}
              >
                {/* Avatar */}
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#10a37f] to-[#0d8a6a]">
                  {selectedAgent?.avatarUrl ? (
                    <img src={selectedAgent.avatarUrl} alt="" className="h-6 w-6 rounded-md object-cover" />
                  ) : (
                    <Bot size={13} className="text-white" />
                  )}
                </div>
                <span
                  className={cn(
                    "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
                    selectedAgent?.status === "active" ? "bg-green-500" : "bg-gray-400",
                  )}
                  title={selectedAgent ? `Agent status: ${selectedAgent.status}` : undefined}
                />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">
                  {selectedAgent?.name ?? "Select agent"}
                </span>
              </button>
              {/* Chevron — opens the agent picker dropdown. Kept as a separate
                  button so the main row can act as the General-channel shortcut
                  without stealing the picker UX. */}
              <button
                onClick={() => setAgentPickerOpen(!agentPickerOpen)}
                data-tour="agent-picker-toggle"
                aria-label="Choose agent"
                aria-haspopup="listbox"
                aria-expanded={agentPickerOpen}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-[#e5e2dc] hover:text-text-primary dark:hover:bg-[#444]"
              >
                <ChevronDown size={12} />
              </button>
            </div>

            {/* Agent dropdown — search-first: the filter input is focused the
                moment the dropdown opens so typing immediately narrows the
                list (essential once a tenant has many agents). */}
            {agentPickerOpen && (
              <div data-tour="agent-picker-list" className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-border bg-background shadow-xl">
                <div className="flex items-center gap-1.5 border-b border-border px-2.5 py-2">
                  <Search size={12} className="shrink-0 text-text-muted" />
                  <input
                    autoFocus
                    value={agentQuery}
                    onChange={(e) => setAgentQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault();
                        setAgentPickerOpen(false);
                      }
                      // Enter selects the single remaining match.
                      if (e.key === "Enter" && filteredAgents.length === 1) {
                        e.preventDefault();
                        syncAgentToUrl(filteredAgents[0].id);
                        setAgentPickerOpen(false);
                      }
                    }}
                    placeholder="Search agents…"
                    aria-label="Search agents"
                    className="w-full bg-transparent text-[12px] text-text-primary placeholder:text-text-muted focus:outline-none"
                  />
                  {agentQuery && (
                    <button
                      onClick={() => setAgentQuery("")}
                      aria-label="Clear agent search"
                      className="shrink-0 text-text-muted hover:text-text-primary"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
                <div className="max-h-60 overflow-y-auto sidebar-scroll">
                {filteredAgents.map((agent) => {
                  const isPinned = pinnedAgentIds.includes(agent.id);
                  return (
                    <div
                      key={agent.id}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setAgentMenu({ agentId: agent.id, x: e.clientX, y: e.clientY });
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 px-2.5 py-2 text-left transition-colors",
                        agent.id === selectedAgentId
                          ? "bg-accent/8 text-accent"
                          : "text-text-secondary hover:bg-surface-hover",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          syncAgentToUrl(agent.id);
                          setAgentPickerOpen(false);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        aria-label={`Select ${agent.name}`}
                      >
                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#10a37f] to-[#0d8a6a]">
                          {agent.avatarUrl ? (
                            <img src={agent.avatarUrl} alt="" className="h-6 w-6 rounded-md object-cover" />
                          ) : (
                            <Bot size={12} className="text-white" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span
                              className={cn(
                                "h-1.5 w-1.5 shrink-0 rounded-full",
                                agent.status === "active" ? "bg-green-500" : "bg-gray-400",
                              )}
                              title={`Agent status: ${agent.status}`}
                            />
                            <p className="truncate text-[12.5px] font-medium">{agent.name}</p>
                          </div>
                          {agentRoleLabel(agent) && (
                            <p className="truncate pl-3 text-[10.5px] text-text-muted">{agentRoleLabel(agent)}</p>
                          )}
                        </div>
                      </button>
                      {isPinned && <Pin size={10} className="shrink-0 text-text-muted" />}
                    </div>
                  );
                })}
                {agents.length === 0 && (
                  <p className="px-3 py-4 text-center text-xs text-text-muted">No agents available</p>
                )}
                {agents.length > 0 && filteredAgents.length === 0 && (
                  <p className="px-3 py-4 text-center text-xs text-text-muted">No agents match "{agentQuery}"</p>
                )}
                </div>
              </div>
            )}
          </div>

          {/* Pinned agents — quick-switch row. Right-click an agent in the
              dropdown or here to toggle the pin. */}
          {pinnedAgents.length > 0 && (
            <div className="mt-2 space-y-0.5">
              <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
                Pinned
              </p>
              {pinnedAgents.map((agent) => (
                <div
                  key={agent.id}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setAgentMenu({ agentId: agent.id, x: e.clientX, y: e.clientY });
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors",
                    agent.id === selectedAgentId
                      ? "bg-accent/8 text-accent"
                      : "text-text-secondary hover:bg-surface-hover",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => syncAgentToUrl(agent.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    title="Right-click to unpin"
                    aria-label={`Select pinned agent ${agent.name}`}
                  >
                    <Pin size={11} className="shrink-0 text-text-muted" />
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        agent.status === "active" ? "bg-green-500" : "bg-gray-400",
                      )}
                      title={`Agent status: ${agent.status}`}
                    />
                    <span className="min-w-0 flex-1 truncate text-[12.5px]">{agent.name}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Channels — capped at 1/4 of the sidebar so Recents keeps
            room; the list scrolls beyond that and can be text-filtered. ── */}
        <div className="mt-1 flex max-h-[25%] min-h-0 flex-col px-3" data-tour="channels">
          <div className="mb-1 flex shrink-0 items-center justify-between px-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
              Channels
            </p>
            {selectedAgentId && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() =>
                    setChannelSearchOpen((o) => {
                      if (o) setChannelQuery("");
                      return !o;
                    })
                  }
                  className={cn(
                    "transition-colors hover:text-text-primary",
                    channelSearchOpen ? "text-text-primary" : "text-text-muted",
                  )}
                  title={channelSearchOpen ? "Close search" : "Search channels"}
                  aria-label={channelSearchOpen ? "Close channel search" : "Search channels"}
                >
                  <Search size={11} />
                </button>
                <button
                  onClick={() => setAddChannelOpen((o) => !o)}
                  className="text-text-muted transition-colors hover:text-text-primary"
                  title={addChannelOpen ? "Cancel" : "New channel"}
                >
                  {addChannelOpen ? <X size={11} /> : <Plus size={11} />}
                </button>
              </div>
            )}
          </div>
          {channelSearchOpen && (
            <div className="mb-1 flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1">
              <Search size={11} className="shrink-0 text-text-muted" />
              <input
                autoFocus
                value={channelQuery}
                onChange={(e) => setChannelQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setChannelSearchOpen(false);
                    setChannelQuery("");
                  }
                }}
                placeholder="Filter channels…"
                aria-label="Filter channels"
                className="w-full bg-transparent text-[11.5px] text-text-primary placeholder:text-text-muted focus:outline-none"
              />
              {channelQuery && (
                <button
                  onClick={() => setChannelQuery("")}
                  aria-label="Clear channel filter"
                  className="shrink-0 text-text-muted hover:text-text-primary"
                >
                  <X size={10} />
                </button>
              )}
            </div>
          )}
          {addChannelOpen && selectedAgentId && (
            <div className="shrink-0">
              <AddChannelForm
                agentId={selectedAgentId}
                onDone={() => setAddChannelOpen(false)}
              />
            </div>
          )}
          <div className="min-h-0 overflow-y-auto sidebar-scroll">
          <nav className="flex flex-col gap-0.5">
            {/* The General channel is reached by clicking the agent name above;
                the Channels list here only shows named channels. Drag-reorder
                is disabled while a filter is active — reordering a partial
                list would scramble the real order. */}
            {visibleChannels.map((ch) => {
              const isChActive = activeChannelId === ch.id;
              const isDragging = dragChannelId === ch.id;
              const isDropTarget = dragOverChannelId === ch.id && dragChannelId !== ch.id;
              return (
                <div
                  key={ch.id}
                  draggable={!channelQ}
                  onDragStart={(e) => {
                    setDragChannelId(ch.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragChannelId && dragOverChannelId !== ch.id) {
                      setDragOverChannelId(ch.id);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleChannelDrop(ch.id);
                  }}
                  onDragEnd={() => {
                    setDragChannelId(null);
                    setDragOverChannelId(null);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setChannelMenu({ channelId: ch.id, x: e.clientX, y: e.clientY });
                  }}
                  className={cn(
                    "group flex cursor-grab items-center gap-2 rounded-md px-[10px] py-1 text-[12.5px] font-[450] transition-colors active:cursor-grabbing",
                    isChActive
                      ? "bg-[#ece9e3]/70 font-[550] text-text-primary dark:bg-[#3a3a3a]"
                      : "text-text-secondary hover:bg-[#efede8] dark:hover:bg-[#333]",
                    isDragging && "opacity-40",
                    isDropTarget && "ring-1 ring-accent/60",
                  )}
                >
                  <button
                    onClick={() => goToChat({ channelId: ch.id })}
                    className="min-w-0 flex-1 flex items-center gap-2 text-left"
                  >
                    <span
                      className="inline-block h-[6px] w-[6px] rounded-full shrink-0"
                      style={{ backgroundColor: isChActive ? "#10a37f" : channelDotColor(ch) }}
                    />
                    <span className="truncate">{ch.name}</span>
                    {ch.shared && (
                      <span className="shrink-0 rounded px-1 py-0.5 text-[10px] font-medium bg-[#e9e6e0] text-[#777] dark:bg-[#3a3a3a] dark:text-[#999]">
                        shared
                      </span>
                    )}
                  </button>
                  {/* Always-visible new-chat plus (subtle until row hover) —
                      replaces the old session-count-then-reveal dance that
                      made the button hard to discover. */}
                  <button
                    onClick={() => goToChat({ channelId: ch.id, newChat: true })}
                    aria-label={`Start a new chat in ${ch.name}`}
                    title="New chat"
                    className="ml-auto flex h-4 w-4 shrink-0 items-center justify-center rounded text-text-muted opacity-60 transition-opacity hover:bg-[#e5e2dc] hover:text-text-primary group-hover:opacity-100 dark:hover:bg-[#444]"
                  >
                    <Plus size={11} />
                  </button>
                </div>
              );
            })}
            {channelQ && visibleChannels.length === 0 && (
              <p className="px-2 py-1.5 text-[11px] text-text-muted">No channels match "{channelQuery}"</p>
            )}
          </nav>
          </div>
        </div>

        {/* ── Recents — filtered by active channel ──────────────────── */}
        <div className="mt-2 flex min-h-0 flex-1 flex-col px-3" data-tour="chats">
          <div className="mb-1 flex shrink-0 items-center justify-between px-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
              Recents
            </p>
            <button
              onClick={() =>
                setRecentsSearchOpen((o) => {
                  if (o) setRecentsQuery("");
                  return !o;
                })
              }
              className={cn(
                "transition-colors hover:text-text-primary",
                recentsSearchOpen ? "text-text-primary" : "text-text-muted",
              )}
              title={recentsSearchOpen ? "Close search" : "Search chats"}
              aria-label={recentsSearchOpen ? "Close chat search" : "Search chats"}
            >
              <Search size={11} />
            </button>
          </div>
          {recentsSearchOpen && (
            <div className="mb-1 flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1">
              <Search size={11} className="shrink-0 text-text-muted" />
              <input
                autoFocus
                value={recentsQuery}
                onChange={(e) => setRecentsQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setRecentsSearchOpen(false);
                    setRecentsQuery("");
                  }
                }}
                placeholder="Filter chats…"
                aria-label="Filter chats"
                className="w-full bg-transparent text-[11.5px] text-text-primary placeholder:text-text-muted focus:outline-none"
              />
              {recentsQuery && (
                <button
                  onClick={() => setRecentsQuery("")}
                  aria-label="Clear chat filter"
                  className="shrink-0 text-text-muted hover:text-text-primary"
                >
                  <X size={10} />
                </button>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto sidebar-scroll">
            <nav className="flex flex-col gap-0.5">
              {visibleRecents.map((s) => {
                const isActive = location.search.includes(s.id);
                const isRenaming = renameState?.sessionId === s.id;
                const isMenuOpen = menuForSessionId === s.id;
                const showUnread = s.hasUnread && !isActive && !s.isGenerating;
                return (
                  <div
                    key={s.id}
                    data-recent-row-menu
                    className={cn(
                      "group relative flex items-center gap-1 rounded-md pr-1 transition-colors hover:bg-[#efede8] dark:hover:bg-[#333]",
                      isActive && !isRenaming && "bg-[#ece9e3]/70 dark:bg-[#3a3a3a]",
                    )}
                  >
                    {isRenaming ? (
                      <input
                        autoFocus
                        type="text"
                        value={renameState.title}
                        onChange={(e) => setRenameState({ ...renameState, title: e.target.value })}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                          if (e.key === "Escape") { e.preventDefault(); setRenameState(null); }
                        }}
                        className="w-full rounded-md border border-accent/30 bg-background px-[10px] py-1 text-[12px] font-medium text-text-primary outline-none focus:border-accent"
                      />
                    ) : (
                      <>
                        <button
                          onClick={() => goToChat({ sessionId: s.id, channelId: s.channelId ?? "general" })}
                          aria-label={`${s.title}${s.isGenerating ? ", response in progress" : showUnread ? ", new response" : ""}`}
                          className={cn(
                            "min-w-0 flex-1 truncate rounded-md px-[10px] py-1 text-left text-[12px] font-normal transition-colors",
                            isActive ? "font-medium text-text-primary" : "text-text-muted",
                          )}
                        >
                          {s.title}
                        </button>
                        <div className="relative h-5 w-5 shrink-0">
                          {(s.isGenerating || showUnread) && (
                            <span
                              className={cn(
                                "absolute inset-0 flex items-center justify-center transition-opacity group-hover:opacity-0",
                                isMenuOpen && "opacity-0",
                              )}
                              title={s.isGenerating ? "Response in progress" : "New response"}
                              aria-hidden="true"
                            >
                              {s.isGenerating ? (
                                <Loader2 size={13} className="animate-spin text-blue-500" />
                              ) : (
                                <span className="h-2 w-2 rounded-full bg-blue-500" />
                              )}
                            </span>
                          )}
                          {/* Status and actions share a fixed slot so updates never shift the title. */}
                          <button
                            aria-label="More actions"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuForSessionId(isMenuOpen ? null : s.id);
                            }}
                            className={cn(
                              "absolute inset-0 flex items-center justify-center rounded text-text-muted transition-opacity hover:bg-black/10 dark:hover:bg-white/10",
                              isMenuOpen
                                ? "opacity-100"
                                : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                            )}
                          >
                            <MoreHorizontal size={13} />
                          </button>
                        </div>
                      </>
                    )}

                    {/* Row action popover */}
                    {isMenuOpen && (
                      <div className="absolute right-1 top-full z-30 mt-1 w-36 overflow-hidden rounded-lg border border-border bg-background shadow-lg">
                        <button
                          onClick={() => {
                            setMenuForSessionId(null);
                            setRenameState({ sessionId: s.id, title: s.title });
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-text-secondary transition-colors hover:bg-surface-hover"
                        >
                          <Pencil size={12} />
                          Rename
                        </button>
                        <button
                          onClick={() => {
                            setMenuForSessionId(null);
                            setConfirmDelete({ sessionId: s.id, title: s.title });
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-red-500 transition-colors hover:bg-red-500/10"
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredRecents.length === 0 && (
                <p className="px-2 py-2 text-[11px] text-text-muted">No chats in this channel</p>
              )}
              {filteredRecents.length > 0 && visibleRecents.length === 0 && (
                <p className="px-2 py-2 text-[11px] text-text-muted">No chats match "{recentsQuery}"</p>
              )}
            </nav>
          </div>
        </div>

        </>)}

        {/* ── Settings mode body (replaces chat nav while active) ───── */}
        {showAdminNav && (<>
        <div className="mx-[14px] mb-2">
          <button
            onClick={() => {
              setSidebarMode("default");
              if (originPathRef.current) {
                navigate(originPathRef.current);
                return;
              }
              const params = new URLSearchParams();
              if (selectedAgentId) params.set("agentId", selectedAgentId);
              const query = params.toString();
              navigate(`${base}/dashboard${query ? `?${query}` : ""}`);
            }}
            className="flex w-full items-center gap-2 rounded-[8px] bg-transparent px-[10px] py-1.5 text-[13px] font-medium text-text-primary transition-colors hover:bg-[#efede8] dark:hover:bg-[#333]"
          >
            <ArrowLeft size={14} className="text-text-secondary" />
            Back to app
          </button>
        </div>
        <div className="mt-1 flex min-h-0 flex-1 flex-col px-3 pb-2">
          <div className="flex-1 overflow-y-auto sidebar-scroll">
            <nav className="flex flex-col gap-0.5">
              {(() => {
                const out: ReactElement[] = [];
                let lastGroup: string | null = null;
                for (const item of moreItems) {
                  if (item.group && item.group !== lastGroup) {
                    out.push(
                      <div
                        key={`group-${item.group}`}
                        className="mt-1.5 px-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted"
                      >
                        {item.group}
                      </div>,
                    );
                    lastGroup = item.group;
                  }
                  const Icon = item.icon;
                  if ("onClick" in item) {
                    out.push(
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => item.onClick?.()}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] font-[450] text-text-secondary transition-colors hover:bg-[#efede8] hover:text-text-primary dark:hover:bg-[#333]"
                      >
                        <Icon size={13} strokeWidth={1.8} />
                        {item.label}
                      </button>,
                    );
                  } else {
                    out.push(
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.label === "Dashboard"}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] font-[450] transition-colors",
                            isActive
                              ? "bg-[#ece9e3]/70 font-medium text-text-primary dark:bg-[#3a3a3a]"
                              : "text-text-secondary hover:bg-[#efede8] dark:hover:bg-[#333]",
                          )
                        }
                      >
                        <Icon size={13} strokeWidth={1.8} />
                        {item.label}
                      </NavLink>,
                    );
                  }
                }
                return out;
              })()}
            </nav>
          </div>
        </div>
        </>)}

        {/* ── User footer ───────────────────────────────────────────── */}
        <div className="relative border-t border-[#e8e5e0] dark:border-[#444]" ref={userMenuRef}>
          <button
            type="button"
            data-tour="profile-menu"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            aria-label="Open user menu"
            aria-expanded={userMenuOpen}
            className="flex w-full items-center gap-2.5 px-3 py-3 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-[12px] font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-[550] text-text-primary">
                {user?.name || "User"}
              </p>
              <p className="truncate text-[11px] text-text-muted">
                {tenantRoleLabel}
              </p>
            </div>
          </button>

          {/* User dropdown menu */}
          {userMenuOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-1 rounded-xl border border-border bg-background shadow-xl z-30">
              <div className="border-b border-border px-3 py-2">
                <p className="text-[12px] font-medium text-text-primary truncate">{user?.name || "User"}</p>
                <p className="text-[11px] text-text-muted truncate">{user?.email}</p>
              </div>
              <div className="p-1">
                {isInstanceAdmin && (
                  <button
                    onClick={() => {
                      // Remember where we came from so the admin shell can
                      // offer a "Back to app" link that returns here.
                      sessionStorage.setItem(
                        "proclaw-admin-origin-path",
                        location.pathname + location.search,
                      );
                      setUserMenuOpen(false);
                      navigate("/dashboard");
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-text-secondary hover:bg-surface-hover transition-colors"
                  >
                    <Building2 size={14} />
                    Platform admin
                  </button>
                )}
                {showAnySettings && (
                  <button
                    data-tour="menu-tenant-settings"
                    onClick={() => {
                      setUserMenuOpen(false);
                      if (onOpenTenantSettings) {
                        onOpenTenantSettings();
                      } else {
                        navigate(isTenantAdmin ? `${base}/settings` : `${base}/settings/mcp`);
                      }
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-text-secondary hover:bg-surface-hover transition-colors"
                  >
                    <Settings size={14} />
                    {isTenantAdmin ? "Tenant settings" : "Settings"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    if (onOpenAccountSettings) {
                      onOpenAccountSettings();
                    } else {
                      navigate("/account");
                    }
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-text-secondary hover:bg-surface-hover transition-colors"
                >
                  <User size={14} />
                  My account
                </button>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    setThemePickerOpen(true);
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-text-secondary hover:bg-surface-hover transition-colors"
                >
                  <Palette size={14} />
                  Appearance
                </button>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    signOut();
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-text-secondary hover:bg-surface-hover transition-colors"
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Agent right-click context menu (pin / unpin) — rendered at the
          root so it's not clipped by the sidebar's overflow. Positioned
          at the cursor; dismissed by the global click listener above. */}
      {agentMenu && menuAgent && (
        <div
          className="fixed z-[100] min-w-[160px] rounded-md border border-border bg-background py-1 text-[12.5px] shadow-lg"
          style={{ top: agentMenu.y, left: agentMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-hover"
            onClick={() => {
              setAgentPinned.mutate({ agentId: menuAgent.id, pinned: !menuAgentPinned });
              setAgentMenu(null);
            }}
          >
            {menuAgentPinned ? <PinOff size={12} /> : <Pin size={12} />}
            {menuAgentPinned ? "Unpin from sidebar" : "Pin to sidebar"}
          </button>
        </div>
      )}

      {/* Channel right-click context menu (delete) — rendered at the root
          so it isn't clipped by the sidebar's overflow. Positioned at the
          cursor; dismissed by the global listeners in the effect above. */}
      {channelMenu && menuChannel && (
        <div
          className="fixed z-[100] min-w-[160px] rounded-md border border-border bg-background py-1 text-[12.5px] shadow-lg"
          style={{ top: channelMenu.y, left: channelMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-red-600 hover:bg-red-500/10 dark:text-red-400"
            onClick={() => {
              setConfirmDeleteChannel({ id: menuChannel.id, name: menuChannel.name });
              setChannelMenu(null);
            }}
          >
            <Trash2 size={12} />
            Delete channel
          </button>
        </div>
      )}

      {/* Theme picker modal */}
      {themePickerOpen && <ThemePicker onClose={() => setThemePickerOpen(false)} />}

      {/* ── Delete confirmation modal ─────────────────────────────── */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-[min(360px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Delete conversation</h3>
            <p className="mb-4 text-xs text-text-secondary">
              Are you sure you want to delete{" "}
              <span className="font-medium text-text-primary">"{confirmDelete.title}"</span>?
              This will permanently remove all messages and cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  deleteSession.mutate(confirmDelete.sessionId);
                  setConfirmDelete(null);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Channel delete confirmation modal ─────────────────────────── */}
      {confirmDeleteChannel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmDeleteChannel(null)}
        >
          <div
            className="w-[min(360px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Delete channel</h3>
            <p className="mb-4 text-xs text-text-secondary">
              Are you sure you want to delete{" "}
              <span className="font-medium text-text-primary">"{confirmDeleteChannel.name}"</span>?
              Chats in this channel won't be deleted — they move back to the
              agent's General area. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteChannel(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteChannelMut.isPending}
                onClick={() => {
                  deleteChannelMut.mutate(confirmDeleteChannel.id);
                  setConfirmDeleteChannel(null);
                }}
              >
                {deleteChannelMut.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AddChannelForm({ agentId, onDone }: { agentId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#10a37f");
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const create = useMutation({
    mutationFn: () => agentChannelsApi.create(agentId, { name: name.trim(), color, shared }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-channels", agentId] });
      onDone();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to create channel");
    },
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <div className="mb-1 rounded-md border border-border bg-[#faf8f4]/70 px-2 py-2 dark:bg-[#2a2a2a]">
      <div className="mb-1.5 flex items-center gap-1.5">
        <input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="h-5 w-5 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
          title="Channel color"
        />
        <input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) create.mutate();
            if (e.key === "Escape") onDone();
          }}
          placeholder="Channel name"
          className="flex-1 rounded bg-transparent text-[12px] text-text-primary placeholder:text-text-muted focus:outline-none"
        />
      </div>
      <label className="mb-1.5 flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={shared}
          onChange={(e) => setShared(e.target.checked)}
          className="h-3 w-3 rounded border-border"
        />
        <span className="text-[10px] text-text-muted">Shared with all users</span>
      </label>
      {error && <p className="mb-1 text-[10px] text-red-500">{error}</p>}
      <div className="flex justify-end gap-1">
        <button
          onClick={onDone}
          className="rounded px-1.5 py-0.5 text-[10px] text-text-muted hover:text-text-primary"
        >
          Cancel
        </button>
        <button
          disabled={!canSubmit}
          onClick={() => create.mutate()}
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-medium",
            canSubmit
              ? "bg-accent text-white hover:bg-accent-hover"
              : "cursor-not-allowed bg-[#e5e2dc] text-text-muted dark:bg-[#3a3a3a]",
          )}
        >
          {create.isPending ? "Creating…" : "Create"}
        </button>
      </div>
    </div>
  );
}
